/* eslint-disable unicorn/prefer-module */
import { useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { BadgeDato, CategoriaLeyendaIpsos, Loader } from "..";
import { IPSOS_VACACIONES, TEMPORADA_OPCIONES, TICKETS_VACACIONES } from "../../data/ipsos2025/vacaciones";
import { useCategoriaMoneda, useUniversoCategorias } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaVacaciones.module.scss";

const COLORES_AEROLINEAS = ["#0d6efd", "#6c757d", "#fd7e14", "#198754"];
const COLORES_DESTINO = ["#0d6efd", "#198754", "#fd7e14"];

const renderPieChart = (data, colors) => {
  let accumulated = 0;
  const segments = data.map(({ share }, i) => {
    const start = accumulated;
    accumulated += share * 100;
    return `${colors[i % colors.length]} ${start}% ${accumulated}%`;
  });
  const gradient = `conic-gradient(${segments.join(", ")})`;

  return (
    <div className={styles.pieChart} style={{ background: gradient }}>
      <div className={styles.pieChartInner} />
    </div>
  );
};

export const Vacaciones = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const {
    edificios,
    loading,
    error,
    checkAll,
    checkedBuildings,
    toggleCheck,
    toggleCheckAll,
    cantidadEdificios,
    totalPantallas,
    totalEjecutivos,
    totalEmpresas,
  } = useUniversoCategorias(pais);
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [ticketPromedio, setTicketPromedio] = useState(TICKETS_VACACIONES[1].valor);
  const [verAnual, setVerAnual] = useState(false);
  const [anoCompleto, setAnoCompleto] = useState(true);
  const [temporadaSeleccionada, setTemporadaSeleccionada] = useState("Verano");

  // =====================================================
  // CÁLCULOS
  // =====================================================
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    const data = IPSOS_VACACIONES;

    const desgloseFrecuencia = {};
    let totalViajesAnuales = 0;

    Object.entries(data.frecuenciaVacaciones).forEach(([freq, info]) => {
      const personas = Math.round(totalEjecutivos * info.pct.valor);
      const viajes = Math.round(personas * info.factor.valor);
      desgloseFrecuencia[freq] = { pct: info.pct.valor, pctDato: info.pct, label: info.label, personas, viajes };
      totalViajesAnuales += viajes;
    });

    const totalViajesMes = Math.round(totalViajesAnuales / 12);

    const temporadaPct = anoCompleto
      ? 1
      : TEMPORADA_OPCIONES.find((t) => t.key === temporadaSeleccionada)?.pct.valor || 1;
    const mercadoTemporadaAnual = Math.round(totalViajesAnuales * temporadaPct * ticketPromedio);
    const mercadoTemporadaMensual = Math.round(mercadoTemporadaAnual / 12);

    const aerolineaData = Object.entries(data.aerolineas)
      .sort(([, a], [, b]) => b.valor - a.valor)
      .map(([nombre, dato]) => ({
        nombre,
        dato,
        share: dato.valor,
        clientes: Math.round(totalEjecutivos * dato.valor),
      }));

    const tipoDestinoData = Object.entries(data.tipoDestino)
      .sort(([, a], [, b]) => b.valor - a.valor)
      .map(([nombre, dato]) => ({
        nombre,
        dato,
        share: dato.valor,
        clientes: Math.round(totalEjecutivos * dato.valor),
      }));

    return {
      desgloseFrecuencia,
      totalViajes: totalViajesMes,
      totalViajesAnuales,
      mercadoTemporada: mercadoTemporadaMensual,
      mercadoTemporadaAnual,
      aerolineaData,
      tipoDestinoData,
    };
  }, [totalEjecutivos, ticketPromedio, anoCompleto, temporadaSeleccionada]);

  if (loading) return <Loader />;
  if (error) return <p className={styles.disclaimer}>{error}</p>;

  const frecuenciaKeys = Object.keys(IPSOS_VACACIONES.frecuenciaVacaciones);

  return (
    <div className={styles.vacacionesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>VACACIONES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <CategoriaLeyendaIpsos />

      <div className={styles.vacacionesContent}>
        {/* Tabla de frecuencia de vacaciones */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thFrecuencia)}>Frecuencia de vacaciones</th>
                  <th className={cx("text-center", styles.thPersonas)}>Según frecuencia</th>
                  <th className={cx("text-center", styles.thConversiones)}>Viajes {verAnual ? "año" : "mes"}</th>
                </tr>
              </thead>
              <tbody>
                {frecuenciaKeys.map((freq) => {
                  const data = resultados.desgloseFrecuencia[freq];
                  return (
                    <tr key={freq}>
                      <td className={styles.celdaFrecuencia}>
                        <div className={styles.frecuenciaInner}>
                          <BadgeDato dato={data.pctDato}>{(data.pct * 100).toFixed(0)}%</BadgeDato>
                          <span>{data.label}</span>
                        </div>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                      <td className="text-center">
                        {freq === "nunca" ? "-" : formatNumber(verAnual ? data.viajes * 12 : data.viajes)}
                      </td>
                    </tr>
                  );
                })}
                <tr className={styles.filaTotales}>
                  <td colSpan={2} className={styles.celdaVacia} />
                  <td className={cx("text-center", styles.celdaTotal)}>
                    <strong>{formatNumber(verAnual ? resultados.totalViajesAnuales : resultados.totalViajes)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Configuración: Temporada + Ticket + Mercado */}
        {resultados && (
          <>
            <div className={styles.configuracionRow}>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabelRow}>
                    <span className={styles.bloqueLabel}>Preferencia por temporada</span>
                    <label className={styles.anoCompletoCheck}>
                      <input type="checkbox" checked={anoCompleto} onChange={(e) => setAnoCompleto(e.target.checked)} />
                      <span>Año completo</span>
                    </label>
                  </div>
                  <div className={cx(styles.temporadaOpciones, anoCompleto && styles.temporadaDisabled)}>
                    {TEMPORADA_OPCIONES.map((t) => (
                      <label key={t.key} className={styles.opcionRadio}>
                        <BadgeDato dato={t.pct}>{(t.pct.valor * 100).toFixed(0)}%</BadgeDato>
                        <span>{t.key}</span>
                        <input
                          type="radio"
                          name="temporada"
                          disabled={anoCompleto}
                          checked={temporadaSeleccionada === t.key}
                          onChange={() => setTemporadaSeleccionada(t.key)}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className={styles.configuracionBloque}>
                <div className={styles.bloqueGris}>
                  <div className={styles.bloqueLabel}>Ticket promedio (supuesto)</div>
                  <div className={styles.ticketOpciones}>
                    {TICKETS_VACACIONES.map((t) => (
                      <label key={t.valor} className={styles.opcionRadio}>
                        <input
                          type="radio"
                          name="ticketPromedio"
                          checked={ticketPromedio === t.valor}
                          onChange={() => setTicketPromedio(t.valor)}
                        />
                        <span>{formatLocal(convertCLP(t.valor))}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.mercadoRow}>
              <div className={styles.mercadoLabelRow}>
                <span className={styles.mercadoLabel}>
                  Mercado {verAnual ? "anual" : "mensual"}
                  {!anoCompleto ? ` (${temporadaSeleccionada})` : ""}
                </span>
                <div className={styles.toggleContainer}>
                  <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                  <label className={styles.toggleSwitch}>
                    <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                    <span className={styles.toggleSlider} />
                  </label>
                  <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                </div>
              </div>
              <div className={styles.mercadoValorGrande}>
                {formatLocal(convertCLP(verAnual ? resultados.mercadoTemporadaAnual : resultados.mercadoTemporada))}
              </div>
            </div>
          </>
        )}

        {/* Gráficos: Aerolínea + Destino */}
        {resultados && (
          <div className={styles.pieChartsRow}>
            <div className={styles.pieChartContainer}>
              <h4>Aerolínea de preferencia</h4>
              <div className={styles.pieChartContent}>
                {renderPieChart(resultados.aerolineaData, COLORES_AEROLINEAS)}
                <div className={styles.pieLegend}>
                  {resultados.aerolineaData.map((d, i) => (
                    <div key={d.nombre} className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: COLORES_AEROLINEAS[i] }} />
                      <span className={styles.legendLabel}>{d.nombre}</span>
                      <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.pieChartContainer}>
              <h4>Tipo de destino</h4>
              <div className={styles.pieChartContent}>
                {renderPieChart(resultados.tipoDestinoData, COLORES_DESTINO)}
                <div className={styles.pieLegend}>
                  {resultados.tipoDestinoData.map((d, i) => (
                    <div key={d.nombre} className={styles.legendItem}>
                      <span className={styles.legendColor} style={{ backgroundColor: COLORES_DESTINO[i] }} />
                      <span className={styles.legendLabel}>{d.nombre}</span>
                      <span className={styles.legendValue}>{(d.share * 100).toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Ipsos 2025 (lámina 100, base 400):</strong> el 60,1% toma vacaciones una vez al año. Latam concentra
            el 82,7% de preferencia en aerolíneas.
          </p>
          <p>
            <strong>Supuestos comerciales:</strong> ticket promedio de viaje y viajes por año por frecuencia.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vacaciones;
